import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageSquare, Clock, Send } from "lucide-react";
import InnerBanner from "../components/InnerBanner";
import serviceBg from "../assets/FFO+.jpeg";
import { adminApi } from "../utils/adminApi";
import "./Contact.css";
import { Helmet } from "react-helmet-async"; // ✅ SEO ONLY ADD

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [issue, setIssue] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const modelsData = {
    Apple: ["iPhone 15", "iPhone 14", "iPhone 13", "iPhone 12"],
    Samsung: ["Galaxy S23", "Galaxy S22", "Galaxy A54"],
    OnePlus: ["OnePlus 11", "OnePlus 10 Pro"],
    Vivo: ["Vivo V29", "Vivo V27"],
    Oppo: ["Oppo Reno 10", "Oppo F23"],
    Realme: ["Realme 11 Pro", "Realme Narzo 60"],
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setSubmitError("");
    setSubmitMessage("");

    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = phone.trim();
    const cleanAddress = address.trim();
    const cleanBrand = brand.trim();
    const cleanModel = model.trim();
    const cleanIssue = issue.trim();
    const cleanDescription = description.trim();

    if (!cleanName || !cleanEmail || !cleanPhone || !cleanBrand || !cleanModel || !cleanIssue) {
      setSubmitError("Please fill all required fields.");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setSubmitError("Please enter a valid 10-digit mobile number.");
      return;
    }

    const formattedMessage = [
      `Brand: ${cleanBrand}`,
      `Model: ${cleanModel}`,
      `Issue: ${cleanIssue}`,
      `Phone: ${cleanPhone}`,
      cleanAddress ? `Address: ${cleanAddress}` : "",
      cleanDescription ? `Note: ${cleanDescription}` : "",
    ]
      .filter(Boolean)
      .join(" | ");

    const payload = {
      name: cleanName,
      email: cleanEmail,
      phone: cleanPhone,
      address: cleanAddress,
      brand: cleanBrand,
      model: cleanModel,
      issue: cleanIssue,
      message: formattedMessage,
    };

    try {
      setIsSubmitting(true);
      await adminApi.post("/api/contact", payload);
      setSubmitMessage("RFQ submitted successfully. Our team will contact you soon.");

      setName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setBrand("");
      setModel("");
      setIssue("");
      setDescription("");
    } catch (error) {
      setSubmitError(error.response?.data?.error || "Unable to submit RFQ. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
<Helmet>
  <title>
    Contact Servizy | Mobile Repair Service Bangalore
  </title>

  <meta
    name="description"
    content="Contact Servizy for fast and reliable mobile repair services in Bangalore. Get doorstep support and expert smartphone repair assistance today."
  />

  <meta
    name="keywords"
    content="contact Servizy, mobile repair contact Bangalore, phone repair support, smartphone repair service, doorstep mobile repair"
  />

  <meta
    name="robots"
    content="index, follow"
  />

  <meta
    name="author"
    content="Servizy"
  />

  <meta
    name="publisher"
    content="Servizy"
  />

  <meta
    property="article:publisher"
    content="https://servizy.co.in/"
  />

  <link
    rel="canonical"
    href="https://servizy.co.in/contact"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="Contact Servizy | Mobile Repair Service Bangalore"
  />

  <meta
    property="og:description"
    content="Get in touch with Servizy for doorstep mobile repair services and expert smartphone repair support in Bangalore."
  />

  <meta
    property="og:image"
    content="https://servizy.co.in/s.png"
  />

  <meta
    property="og:url"
    content="https://servizy.co.in/contact"
  />

  <meta
    property="og:type"
    content="website"
  />

  <meta
    property="og:site_name"
    content="Servizy"
  />

  {/* Twitter */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content="Contact Servizy | Mobile Repair Service Bangalore"
  />

  <meta
    name="twitter:description"
    content="Fast and reliable doorstep mobile repair services in Bangalore with expert technicians."
  />

  <meta
    name="twitter:image"
    content="https://servizy.co.in/s.png"
  />

  {/* Geo SEO */}
  <meta
    name="geo.region"
    content="IN-KA"
  />

  <meta
    name="geo.placename"
    content="Bangalore"
  />

  <meta
    name="language"
    content="English"
  />

  {/* Mobile SEO */}
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  />

  {/* Theme Color */}
  <meta
    name="theme-color"
    content="#0a84ff"
  />
</Helmet>
      <br />
      <br />
      <InnerBanner title="Contact Us" bgImage={serviceBg} />

      <div className="contact-wrapper">
        <div className="contact-grid">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="contact-info-column"
          >
            <div className="status-pill">
              <span className="pulse-dot"></span>
              Technicians Online
            </div>

            <h1 className="hero-text">
              Let's Fix It <span className="blue-accent">Today.</span>
            </h1>

            <p className="hero-description">
              Get a free diagnostic from our certified experts.
              Most repairs are finished in under 2 hours.
            </p>

            <div className="method-stack">
              <a href="tel:+919876543210" className="method-card">
                <div className="icon-wrapper">
                  <Phone size={24} />
                </div>

                <div className="method-details">
                  <span className="method-label">CALL US</span>
                  <p className="method-value">+91 98765 43210</p>
                </div>
              </a>

              <a href="https://wa.me/919876543210" className="method-card">
                <div className="icon-wrapper">
                  <MessageSquare size={24} />
                </div>

                <div className="method-details">
                  <span className="method-label">WHATSAPP</span>
                  <p className="method-value">Chat with Support</p>
                </div>
              </a>

              <div className="method-card no-hover">
                <div className="icon-wrapper">
                  <Clock size={24} />
                </div>

                <div className="method-details">
                  <span className="method-label">STORE HOURS</span>
                  <p className="method-value">Mon - Sat: 10AM - 8PM</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="form-card"
          >
            <h3 className="form-header">Repair Ticket</h3>

            <form className="repair-form" onSubmit={handleSubmit}>
              <div className="floating-group">
                <input
                  type="text"
                  placeholder=" "
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  style={{ color: "black" }}
                />
                <label className={name ? "label-active" : ""}>Full Name</label>
              </div>

              <div className="form-row">
                <div className="floating-group">
                  <input
                    type="tel"
                    placeholder=" "
                    value={phone}
                    onChange={(event) =>
                      setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    required
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    style={{ color: "black" }}
                  />
                  <label className={phone ? "label-active" : ""}>Mobile Number</label>
                </div>

                <div className="floating-group">
                  <input
                    type="text"
                    placeholder=" "
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    style={{ color: "black" }}
                  />
                  <label className={address ? "label-active" : ""}>Address</label>
                </div>
              </div>

              <div className="form-row">
                <div className="floating-group">
                  <input
                    type="email"
                    placeholder=" "
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    style={{ color: "black" }}
                  />
                  <label className={email ? "label-active" : ""}>Email</label>
                </div>
              </div>

              <div className="form-row">
                <div className="floating-group">
                  <select
                    value={brand}
                    onChange={(event) => {
                      setBrand(event.target.value);
                      setModel("");
                    }}
                    required
                  >
                    <option value="" disabled hidden></option>
                    {Object.keys(modelsData).map((brandName) => (
                      <option key={brandName} value={brandName}>
                        {brandName}
                      </option>
                    ))}
                  </select>
                  <label className={brand ? "label-active" : ""}>Brand</label>
                </div>

                <div className="floating-group">
                  <select
                    value={model}
                    onChange={(event) => setModel(event.target.value)}
                    required
                    disabled={!brand}
                  >
                    <option value="" disabled hidden></option>
                    {brand &&
                      modelsData[brand].map((modelName) => (
                        <option key={modelName} value={modelName}>
                          {modelName}
                        </option>
                      ))}
                  </select>
                  <label className={model ? "label-active" : ""}>Model</label>
                </div>
              </div>

              <div className="floating-group">
                <select value={issue} onChange={(event) => setIssue(event.target.value)} required>
                  <option value="" disabled hidden></option>
                  <option value="Cracked Screen">Cracked Screen Repair</option>
                  <option value="Battery Replacement">Battery Replacement</option>
                  <option value="Water Damage">Water Damage Repair</option>
                  <option value="Charging Port Repair">Charging Port Repair</option>
                  <option value="Speaker Repair">Speaker Repair</option>
                  <option value="Microphone Repair">Microphone Repair</option>
                  <option value="Camera Repair">Camera Repair</option>
                  <option value="Back Glass Repair">Back Glass Repair</option>
                  <option value="Power Button Repair">Power Button Repair</option>
                  <option value="Volume Button Repair">Volume Button Repair</option>
                  <option value="Software Issue">Software Issues / Update</option>
                  <option value="Phone Not Turning On">Phone Not Turning On</option>
                  <option value="Overheating Issue">Overheating Issue</option>
                </select>
                <label className={issue ? "label-active" : ""}>Issue Type</label>
              </div>

              <div className="floating-group">
                <textarea
                  placeholder=" "
                  rows="3"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                ></textarea>
                <label className={description ? "label-active" : ""}>Description</label>
              </div>

              {submitMessage ? (
                <p className="form-feedback success" role="status" aria-live="polite">
                  {submitMessage}
                </p>
              ) : null}

              {submitError ? (
                <p className="form-feedback error" role="alert" aria-live="assertive">
                  {submitError}
                </p>
              ) : null}

              <button type="submit" className="prime-btn" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Request Quote"} <Send size={18} />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Contact;
